import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { DeskDetail, DeskStatus, StatusKind } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge, Dialog } from '@/components/ui/misc';
import { errorMessage } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';

const KIND_LABEL: Record<StatusKind, string> = {
  open: 'aberto',
  closed: 'fechado',
  canceled: 'cancelado',
  custom: 'personalizado',
};

const KIND_VARIANT: Record<StatusKind, 'info' | 'success' | 'destructive' | 'default'> = {
  open: 'info',
  closed: 'success',
  canceled: 'destructive',
  custom: 'default',
};

export function DeskStatusesSection({ desk }: { desk: DeskDetail }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ status: DeskStatus | null } | null>(null);

  const remove = useMutation({
    mutationFn: async (statusId: number) => {
      const res = await api<void>(`/desks/${desk.id}/statuses/${statusId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] }),
  });

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">Statuses</h2>
          <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
            {desk.statuses.length}
          </span>
        </div>
        <Button size="sm" onClick={() => setDialog({ status: null })}>
          <Plus className="size-4" strokeWidth={2.5} />
          Status
        </Button>
      </div>
      <ul className="space-y-1.5">
        {desk.statuses.map((s) => (
          <li
            key={s.id}
            className="flex items-center justify-between gap-2 rounded-md border border-divider px-3 py-2 text-sm transition-colors hover:border-border"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate font-medium">{s.name}</span>
              <Badge variant={KIND_VARIANT[s.kind]}>{KIND_LABEL[s.kind]}</Badge>
            </div>
            {s.kind === 'custom' ? (
              <div className="flex shrink-0 items-center gap-0.5">
                <DeskRowAction label="Renomear status" onClick={() => setDialog({ status: s })}>
                  <Pencil className="size-3.5" />
                </DeskRowAction>
                <DeskRowAction
                  label="Remover status"
                  destructive
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Remover o status "${s.name}"?`)) remove.mutate(s.id);
                  }}
                >
                  <Trash2 className="size-3.5" />
                </DeskRowAction>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {remove.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}
      {dialog ? (
        <StatusDialog deskId={desk.id} status={dialog.status} onClose={() => setDialog(null)} />
      ) : null}
    </section>
  );
}

function StatusDialog({
  deskId,
  status,
  onClose,
}: {
  deskId: number;
  status: DeskStatus | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(status?.name ?? '');

  const save = useMutation({
    mutationFn: async (body: { name: string }) => {
      const res = status
        ? await api<DeskStatus>(`/desks/${deskId}/statuses/${status.id}`, {
            method: 'PUT',
            body,
          })
        : await api<DeskStatus>(`/desks/${deskId}/statuses`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate({ name: name.trim() });
  };

  return (
    <Dialog open onClose={onClose} title={status ? 'Renomear status' : 'Novo status'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="status-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="status-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
